import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "react-toastify";
import {
  Loader2,
  MapPin,
  Plus,
  Users,
  UserPlus,
  Package,
  Route,
  Pencil,
  Eye,
  Trash2,
} from "lucide-react";
import { api } from "../../lib/axios";
import type { Equipe, Zone } from "../../types/planificateur";
import ModalEquipe from "../../components/ModalEquipe";
import ModalEditEquipe from "../../components/ModalEditEquipe";
import OrganisationTerrainWorkflow from "./OrganisationTerrainWorkflow";
import ModalZoneOrganisationCreate from "./ModalZoneOrganisationCreate";
import AffecterAgentsEquipeDrawer from "./AffecterAgentsEquipeDrawer";
import AffecterRessourcesEquipeDrawer from "./AffecterRessourcesEquipeDrawer";

type EquipeRow = Equipe & {
  actif?: boolean;
  capaciteJour?: number;
  chef?: { id: number; nom: string; prenom?: string | null } | null;
  _count?: { agents?: number; tournees?: number };
};

export default function OrganisationTerrainPage(): React.JSX.Element {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const [loading, setLoading] = useState(true);
  const [zones, setZones] = useState<Zone[]>([]);
  const [zonesError, setZonesError] = useState(false);
  const [equipes, setEquipes] = useState<EquipeRow[]>([]);
  const [agentsSansEquipe, setAgentsSansEquipe] = useState(0);
  const [tourneeCountByEquipe, setTourneeCountByEquipe] = useState<Map<number, number>>(
    () => new Map()
  );
  const [tourneesJourTotal, setTourneesJourTotal] = useState(0);
  const [ressourcesAffectees, setRessourcesAffectees] = useState(0);
  const [ressourceCountByEquipe, setRessourceCountByEquipe] = useState<Map<number, number>>(
    () => new Map()
  );

  const [filtreZoneNom, setFiltreZoneNom] = useState("");
  const [search, setSearch] = useState("");

  const [modalZone, setModalZone] = useState(false);
  const [modalEquipe, setModalEquipe] = useState(false);
  const [modalEdit, setModalEdit] = useState<EquipeRow | null>(null);
  const [drawerAgents, setDrawerAgents] = useState(false);
  const [drawerAgentsEquipeId, setDrawerAgentsEquipeId] = useState<number | null>(null);
  const [drawerRes, setDrawerRes] = useState(false);
  const [drawerResEquipeId, setDrawerResEquipeId] = useState<number | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setZonesError(false);
    let zOk = true;
    try {
      const zRes = await api.get("/zones");
      const zonesData = Array.isArray(zRes.data) ? zRes.data : (zRes.data as { data?: Zone[] })?.data ?? [];
      setZones(zonesData as Zone[]);
    } catch {
      zOk = false;
      setZones([]);
      setZonesError(true);
      toast.error("Impossible de charger les zones.", { toastId: "org-terrain-zones-err" });
    }

    try {
      const todayIso = new Date().toISOString().slice(0, 10);
      const [eqRes, tourRes, agRes, resRes] = await Promise.all([
        api.get("/equipes"),
        api.get("/tournees", { params: { date: todayIso, limit: 100, page: 1 } }).catch(() => ({
          data: { items: [] },
        })),
        api.get("/agents").catch(() => ({ data: [] })),
        api.get("/ressources-operationnelles").catch(() => ({ data: { data: [] } })),
      ]);

      const equipesData = Array.isArray(eqRes.data)
        ? eqRes.data
        : (eqRes.data as { data?: EquipeRow[] })?.data ?? [];
      setEquipes(equipesData as EquipeRow[]);

      const items = ((tourRes.data as { items?: { equipe?: { id?: number } | null }[] })?.items ??
        []) as { equipe?: { id?: number } | null }[];
      setTourneesJourTotal(items.length);
      const m = new Map<number, number>();
      for (const t of items) {
        const eid = t.equipe?.id;
        if (eid != null) m.set(eid, (m.get(eid) ?? 0) + 1);
      }
      setTourneeCountByEquipe(m);

      const resPayload = resRes.data as { data?: { id: number; equipeId?: number | null }[] };
      const resRows = Array.isArray(resPayload?.data) ? resPayload.data : [];
      const aff = resRows.filter((r) => r.equipeId != null);
      setRessourcesAffectees(aff.length);
      const rm = new Map<number, number>();
      for (const r of aff) {
        if (r.equipeId != null) rm.set(r.equipeId, (rm.get(r.equipeId) ?? 0) + 1);
      }
      setRessourceCountByEquipe(rm);

      if (zOk) {
        const agentsArr = Array.isArray(agRes.data) ? agRes.data : [];
        setAgentsSansEquipe(
          agentsArr.filter(
            (a: { actif?: boolean; equipeId?: number | null }) => a.actif && !a.equipeId
          ).length
        );
      } else {
        setAgentsSansEquipe(0);
      }
    } catch {
      toast.error("Impossible de charger les équipes.", { toastId: "org-terrain-eq-err" });
      setEquipes([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const openEquipeFlag = searchParams.get("openEquipe");
  useEffect(() => {
    if (openEquipeFlag !== "1") return;
    setModalEquipe(true);
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        next.delete("openEquipe");
        return next;
      },
      { replace: true }
    );
  }, [openEquipeFlag, setSearchParams]);

  const kpi = useMemo(() => {
    if (zonesError) {
      return {
        zonesActives: null as number | null,
        equipesActives: null as number | null,
        agentsAffectes: null as number | null,
        agentsSansEquipe: null as number | null,
        ressourcesAffectees: null as number | null,
        tourneesJour: null as number | null,
      };
    }
    const zonesActives = zones.filter((z) => z.actif !== false).length;
    const equipesActives = equipes.filter((e) => e.actif !== false).length;
    let agentsAffectes = 0;
    for (const e of equipes) {
      const n = Array.isArray(e.agents)
        ? e.agents.length
        : e._count?.agents ?? 0;
      agentsAffectes += n;
    }
    return {
      zonesActives,
      equipesActives,
      agentsAffectes,
      agentsSansEquipe,
      ressourcesAffectees,
      tourneesJour: tourneesJourTotal,
    };
  }, [zones, equipes, agentsSansEquipe, zonesError, ressourcesAffectees, tourneesJourTotal]);

  const listeGlobale = useMemo(() => {
    return equipes
      .filter(
        (e) =>
          (!filtreZoneNom || e.zone?.nom === filtreZoneNom) &&
          e.nom.toLowerCase().includes(search.toLowerCase())
      )
      .map((e) => ({
        ...e,
        zoneLabel: e.zone?.nom || "—",
      }));
  }, [equipes, filtreZoneNom, search]);

  const openAgents = (equipeId?: number | null) => {
    setDrawerAgentsEquipeId(equipeId ?? null);
    setDrawerAgents(true);
  };

  const openRes = (equipeId?: number | null) => {
    setDrawerResEquipeId(equipeId ?? null);
    setDrawerRes(true);
  };

  const handleSupprimer = async (id: number) => {
    if (!window.confirm("Supprimer cette équipe ?")) return;
    try {
      await api.delete(`/equipes/${id}`);
      toast.success("Équipe supprimée.", { toastId: "org-terrain-del-ok" });
      void load();
    } catch (err: unknown) {
      const ax = err as { response?: { data?: { error?: string } } };
      toast.error(ax.response?.data?.error ?? "Suppression impossible.", {
        toastId: "org-terrain-del-err",
      });
    }
  };

  const tourneesHref = "/admin/planificateur/tournees";
  const agentsListeHref = "/admin/agents";

  return (
    <div className="space-y-6 text-slate-100">
      <div className="sticky top-0 z-20 -mx-4 flex flex-wrap items-start justify-between gap-4 border-b border-slate-800/80 bg-slate-950/95 px-4 pb-4 pt-1 backdrop-blur-md lg:mx-0 lg:border-0 lg:bg-transparent lg:p-0 lg:backdrop-blur-none">
        <div>
          <div className="mb-2 flex flex-wrap items-center gap-2">
            <Link
              to="/admin/planificateur"
              className="inline-flex items-center gap-2 text-xs font-medium text-slate-400 hover:text-emerald-400"
            >
              Planification
            </Link>
          </div>
          <h1 className="text-xl font-semibold tracking-tight text-white">Organisation terrain</h1>
          <p className="mt-1 max-w-2xl text-sm text-slate-400">
            Structurez vos zones, équipes, agents et ressources avant de générer les tournées.
          </p>
        </div>
        <div className="flex flex-wrap justify-end gap-2">
          <button
            type="button"
            onClick={() => setModalZone(true)}
            className="inline-flex items-center gap-2 rounded-xl border border-slate-600 bg-slate-900 px-3 py-2 text-sm font-medium text-slate-200 hover:bg-slate-800"
          >
            <MapPin className="h-4 w-4 text-emerald-400" />
            Créer une zone
          </button>
          <button
            type="button"
            onClick={() => {
              if (zonesError) {
                toast.warning("Zones indisponibles.", { toastId: "org-terrain-no-z" });
                return;
              }
              if (zones.length === 0) {
                toast.warning("Créez d’abord une zone.", { toastId: "org-terrain-no-z2" });
                return;
              }
              setModalEquipe(true);
            }}
            className="inline-flex items-center gap-2 rounded-xl bg-emerald-600 px-3 py-2 text-sm font-semibold text-white shadow-lg shadow-emerald-900/20 hover:bg-emerald-500 disabled:opacity-50"
            disabled={zonesError}
          >
            <Plus className="h-4 w-4" />
            Créer une équipe
          </button>
          <button
            type="button"
            onClick={() => openAgents(null)}
            className="inline-flex items-center gap-2 rounded-xl border border-slate-600 bg-slate-900 px-3 py-2 text-sm font-medium text-slate-200 hover:bg-slate-800"
          >
            <UserPlus className="h-4 w-4 text-sky-400" />
            Affecter agents
          </button>
          <button
            type="button"
            onClick={() => navigate(tourneesHref)}
            className="inline-flex items-center gap-2 rounded-xl border border-slate-600 bg-slate-900 px-3 py-2 text-sm font-medium text-slate-200 hover:bg-slate-800"
          >
            <Route className="h-4 w-4 text-amber-400" />
            Voir tournées
          </button>
        </div>
      </div>

      {loading ? (
        <div className="flex min-h-[30vh] items-center justify-center gap-2 text-slate-400">
          <Loader2 className="h-5 w-5 animate-spin" />
          Chargement…
        </div>
      ) : (
        <>
          <div className="grid grid-cols-2 gap-3 lg:grid-cols-3 xl:grid-cols-6">
            <KpiCard
              label="Zones actives"
              value={kpi.zonesActives}
              actionLabel={kpi.zonesActives === 0 ? "Créer une zone" : undefined}
              onAction={kpi.zonesActives === 0 ? () => setModalZone(true) : undefined}
            />
            <KpiCard
              label="Équipes actives"
              value={kpi.equipesActives}
              actionLabel={kpi.equipesActives === 0 ? "Créer une équipe" : undefined}
              onAction={
                kpi.equipesActives === 0 && !zonesError && zones.length > 0
                  ? () => setModalEquipe(true)
                  : kpi.equipesActives === 0
                    ? () => setModalZone(true)
                    : undefined
              }
            />
            <KpiCard label="Agents affectés" value={kpi.agentsAffectes} />
            <KpiCard
              label="Agents sans équipe"
              value={kpi.agentsSansEquipe}
              actionLabel={
                kpi.agentsSansEquipe != null && kpi.agentsSansEquipe > 0
                  ? "Affecter agents"
                  : undefined
              }
              onAction={
                kpi.agentsSansEquipe != null && kpi.agentsSansEquipe > 0
                  ? () => openAgents(null)
                  : undefined
              }
              secondaryLink={agentsListeHref}
              secondaryLabel="Liste RH"
            />
            <KpiCard
              label="Ressources affectées"
              value={kpi.ressourcesAffectees}
              actionLabel={
                kpi.ressourcesAffectees === 0 && kpi.equipesActives != null && kpi.equipesActives > 0
                  ? "Affecter ressources"
                  : undefined
              }
              onAction={
                kpi.ressourcesAffectees === 0 && kpi.equipesActives != null && kpi.equipesActives > 0
                  ? () => openRes(null)
                  : undefined
              }
            />
            <KpiCard
              label="Tournées du jour"
              value={kpi.tourneesJour}
              actionLabel="Voir"
              onAction={() => navigate(tourneesHref)}
            />
          </div>

          <OrganisationTerrainWorkflow
            zoneCount={zones.length}
            equipeCount={equipes.length}
            agentsAffectes={kpi.agentsAffectes ?? 0}
            agentsSansEquipe={agentsSansEquipe}
            ressourcesAffectees={ressourcesAffectees}
            tourneesJour={tourneesJourTotal}
            onCreateZone={() => setModalZone(true)}
            onCreateEquipe={() => {
              if (zones.length === 0) {
                setModalZone(true);
                return;
              }
              setModalEquipe(true);
            }}
            onAffecterAgents={() => openAgents(null)}
            onAffecterRessources={() => openRes(null)}
            onGenererTournees={() => navigate(tourneesHref)}
          />

          {equipes.length === 0 ? (
            <div className="rounded-2xl border border-slate-800 bg-gradient-to-br from-slate-900/90 to-slate-950 p-10 text-center shadow-xl ring-1 ring-white/5">
              <Users className="mx-auto h-14 w-14 text-slate-600" />
              <h2 className="mt-4 text-xl font-semibold text-white">Aucune équipe créée</h2>
              <p className="mx-auto mt-2 max-w-lg text-sm text-slate-400">
                Les équipes permettent d’organiser les passages, les tournées et les interventions terrain.
              </p>
              <div className="mt-8 flex flex-wrap justify-center gap-3">
                <button
                  type="button"
                  onClick={() => setModalEquipe(true)}
                  className="rounded-xl bg-emerald-600 px-5 py-2.5 text-sm font-semibold text-white hover:bg-emerald-500"
                >
                  Créer une équipe
                </button>
                <button
                  type="button"
                  onClick={() => setModalZone(true)}
                  className="rounded-xl border border-slate-600 px-5 py-2.5 text-sm font-medium text-slate-200 hover:bg-slate-800"
                >
                  Créer une zone
                </button>
                <Link
                  to={agentsListeHref}
                  className="rounded-xl border border-slate-600 px-5 py-2.5 text-sm font-medium text-slate-200 hover:bg-slate-800"
                >
                  Voir agents sans équipe
                </Link>
              </div>
            </div>
          ) : (
            <>
              <div className="flex flex-wrap gap-3 rounded-xl border border-slate-800 bg-slate-950/40 p-4">
                <input
                  type="search"
                  placeholder="Rechercher une équipe…"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  className="min-w-[200px] flex-1 rounded-lg border border-slate-700 bg-slate-950 px-3 py-2 text-sm text-white placeholder:text-slate-600 focus:border-emerald-500/50 focus:outline-none"
                />
                <select
                  value={filtreZoneNom}
                  onChange={(e) => setFiltreZoneNom(e.target.value)}
                  className="rounded-lg border border-slate-700 bg-slate-950 px-3 py-2 text-sm text-white focus:border-emerald-500/50 focus:outline-none"
                >
                  <option value="">Toutes les zones</option>
                  {zones.map((z) => (
                    <option key={z.id} value={z.nom}>
                      {z.nom}
                    </option>
                  ))}
                </select>
              </div>

              <div className="overflow-x-auto rounded-xl border border-slate-800 shadow-inner ring-1 ring-black/20">
                <table className="min-w-[960px] w-full border-collapse text-left text-sm">
                  <thead>
                    <tr className="border-b border-slate-800 bg-slate-900/80 text-xs font-semibold uppercase tracking-wide text-slate-500">
                      <th className="px-4 py-3">Équipe</th>
                      <th className="px-4 py-3">Zone</th>
                      <th className="px-4 py-3">Chef</th>
                      <th className="px-4 py-3">Agents</th>
                      <th className="px-4 py-3">Ressources</th>
                      <th className="px-4 py-3">Capacité jour</th>
                      <th className="px-4 py-3">Tournées du jour</th>
                      <th className="px-4 py-3">Statut</th>
                      <th className="px-4 py-3 text-right">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {listeGlobale.map((e) => {
                      const nAgents = Array.isArray(e.agents)
                        ? e.agents.length
                        : e._count?.agents ?? 0;
                      const chefLabel = e.chef
                        ? `${e.chef.prenom ?? ""} ${e.chef.nom}`.trim()
                        : "—";
                      const actif = e.actif !== false;
                      return (
                        <tr key={e.id} className="border-b border-slate-800/80 hover:bg-slate-900/50">
                          <td className="px-4 py-3 font-medium text-white">{e.nom}</td>
                          <td className="px-4 py-3 text-slate-300">{e.zoneLabel}</td>
                          <td className="px-4 py-3 text-slate-400">{chefLabel}</td>
                          <td className="px-4 py-3 tabular-nums text-slate-300">{nAgents}</td>
                          <td className="px-4 py-3 tabular-nums text-slate-300">
                            {ressourceCountByEquipe.get(e.id) ?? 0}
                          </td>
                          <td className="px-4 py-3 tabular-nums text-slate-300">
                            {e.capaciteJour ?? 0}
                          </td>
                          <td className="px-4 py-3 tabular-nums text-slate-300">
                            {tourneeCountByEquipe.get(e.id) ?? 0}
                          </td>
                          <td className="px-4 py-3">
                            <span
                              className={
                                actif
                                  ? "rounded-full bg-emerald-500/15 px-2 py-0.5 text-xs font-medium text-emerald-400"
                                  : "rounded-full bg-slate-700 px-2 py-0.5 text-xs text-slate-400"
                              }
                            >
                              {actif ? "Actif" : "Inactif"}
                            </span>
                          </td>
                          <td className="px-4 py-3">
                            <div className="flex flex-wrap justify-end gap-1.5">
                              <button
                                type="button"
                                onClick={() => navigate(`/admin/equipes/${e.id}`)}
                                className="inline-flex items-center gap-1 rounded-lg border border-slate-700 px-2 py-1 text-xs text-slate-300 hover:bg-slate-800"
                              >
                                <Eye className="h-3.5 w-3.5" />
                                Fiche
                              </button>
                              <button
                                type="button"
                                onClick={() => openAgents(e.id)}
                                className="inline-flex items-center gap-1 rounded-lg border border-slate-700 px-2 py-1 text-xs text-slate-300 hover:bg-slate-800"
                              >
                                <Users className="h-3.5 w-3.5" />
                                Agents
                              </button>
                              <button
                                type="button"
                                onClick={() => openRes(e.id)}
                                className="inline-flex items-center gap-1 rounded-lg border border-slate-700 px-2 py-1 text-xs text-slate-300 hover:bg-slate-800"
                              >
                                <Package className="h-3.5 w-3.5" />
                                Ressources
                              </button>
                              <button
                                type="button"
                                onClick={() => navigate(tourneesHref)}
                                className="inline-flex items-center gap-1 rounded-lg border border-slate-700 px-2 py-1 text-xs text-slate-300 hover:bg-slate-800"
                              >
                                <Route className="h-3.5 w-3.5" />
                                Tournée
                              </button>
                              <button
                                type="button"
                                onClick={() => setModalEdit(e)}
                                className="inline-flex items-center gap-1 rounded-lg border border-slate-700 px-2 py-1 text-xs text-slate-300 hover:bg-slate-800"
                              >
                                <Pencil className="h-3.5 w-3.5" />
                                Modifier
                              </button>
                              <button
                                type="button"
                                onClick={() => void handleSupprimer(e.id)}
                                className="inline-flex items-center gap-1 rounded-lg border border-rose-900/50 px-2 py-1 text-xs text-rose-400 hover:bg-rose-950/40"
                              >
                                <Trash2 className="h-3.5 w-3.5" />
                              </button>
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </>
          )}
        </>
      )}

      <ModalZoneOrganisationCreate
        open={modalZone}
        onClose={() => setModalZone(false)}
        onCreated={() => void load()}
      />

      {modalEquipe ? (
        <ModalEquipe
          zones={zones}
          zonesLoadError={zonesError}
          onClose={() => setModalEquipe(false)}
          onSuccess={() => void load()}
          onOpenZoneCreate={() => {
            setModalEquipe(false);
            setModalZone(true);
          }}
          panelClassName="max-w-3xl"
          showCommentField
        />
      ) : null}

      {modalEdit ? (
        <ModalEditEquipe equipe={modalEdit} onClose={() => setModalEdit(null)} onSuccess={() => void load()} />
      ) : null}

      <AffecterAgentsEquipeDrawer
        open={drawerAgents}
        onClose={() => setDrawerAgents(false)}
        onSaved={() => void load()}
        equipes={equipes}
        initialEquipeId={drawerAgentsEquipeId}
      />

      <AffecterRessourcesEquipeDrawer
        open={drawerRes}
        onClose={() => setDrawerRes(false)}
        onSaved={() => void load()}
        equipes={equipes}
        initialEquipeId={drawerResEquipeId}
      />
    </div>
  );
}

function KpiCard({
  label,
  value,
  actionLabel,
  onAction,
  secondaryLink,
  secondaryLabel,
}: {
  label: string;
  value: number | null;
  actionLabel?: string;
  onAction?: () => void;
  secondaryLink?: string;
  secondaryLabel?: string;
}): React.JSX.Element {
  return (
    <div className="rounded-xl border border-slate-800 bg-slate-950/60 p-4 ring-1 ring-white/5">
      <p className="text-[11px] font-semibold uppercase tracking-wide text-slate-500">{label}</p>
      <p className="mt-1 text-2xl font-bold tabular-nums text-white">{value === null ? "—" : value}</p>
      {actionLabel && onAction ? (
        <button
          type="button"
          onClick={onAction}
          className="mt-2 text-xs font-medium text-emerald-400 hover:text-emerald-300"
        >
          {actionLabel}
        </button>
      ) : null}
      {secondaryLink ? (
        <Link to={secondaryLink} className="mt-1 block text-xs text-slate-500 hover:text-slate-300">
          {secondaryLabel ?? "Ouvrir"}
        </Link>
      ) : null}
    </div>
  );
}
